import React from 'react';

/**
 * Page scenes for the preview.
 *
 * These are faithful compositions of real screens, not the live routes. Mounting
 * the actual page components here would fetch, write table settings under the
 * user's own settingsKey, register hotkeys and drag half the app's providers into
 * an editor - and would still show whatever data happened to exist today. A scene
 * is deterministic, inert and always full: every status colour present, an empty
 * state, a disabled control, a focused field.
 *
 * What matters is that the MARKUP is the app's markup - the same token classes
 * the real screens use - because that is what the preview and the inspector are
 * both reading. `data-theme-part` labels name a region in the readout; they are
 * for orientation only and never affect what is painted.
 */

export interface Scene {
  id: string;
  /** Shown in the picker, matching the real page's name in the nav. */
  label: string;
  /** Which part of the app this shape covers, for the picker's second line. */
  hint: string;
  render: () => React.ReactNode;
}

/* ------------------------------------------------------------------ pieces */

type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'flag';

/**
 * Written out rather than built as `bg-${tone}`: Tailwind scans source text, so
 * an interpolated class name is never compiled and the pill renders unstyled.
 */
const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-ok text-ok-fg',
  warn: 'bg-warn text-warn-fg',
  danger: 'bg-danger text-danger-fg',
  info: 'bg-info text-info-fg',
  flag: 'bg-flag text-flag-fg',
};

const Pill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    data-theme-part={`Status pill (${tone})`}
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[tone]}`}
  >
    {children}
  </span>
);

const PrimaryButton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <button
    type="button"
    data-theme-part="Primary button"
    className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-accent-fg hover:bg-accent-hover"
  >
    {children}
  </button>
);

const GhostButton: React.FC<{ children: React.ReactNode; disabled?: boolean }> = ({ children, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    data-theme-part={disabled ? 'Button (disabled)' : 'Secondary button'}
    className="rounded border border-rule bg-panel px-3 py-1.5 text-xs text-body hover:bg-highlight disabled:text-disabled"
  >
    {children}
  </button>
);

/** The dropdown row every list page carries above its grid. */
const FilterRow: React.FC<{ filters: string[] }> = ({ filters }) => (
  <div
    data-theme-part="Filter bar"
    className="flex flex-wrap items-center gap-2 border-b border-rule bg-panel px-3 py-2"
  >
    <span className="text-cmp-field-label">Filters:</span>
    {filters.map((f) => (
      <span
        key={f}
        data-theme-part="Form field"
        className="flex h-6 items-center gap-2 rounded border border-cmp-field-edge bg-cmp-field px-2 text-cmp-field-fg"
      >
        {f}<span className="text-muted">⌄</span>
      </span>
    ))}
  </div>
);

interface GridProps {
  title: string;
  columns: string[];
  rows: React.ReactNode[][];
  /** Renders the zebra + hover treatment on the second row. */
  hoverRow?: number;
}

const Grid: React.FC<GridProps> = ({ title, columns, rows, hoverRow = 1 }) => (
  <div data-theme-part="Data grid" className="overflow-hidden">
    <div
      data-theme-part="Grid title bar"
      className="flex items-center justify-between bg-cmp-table-titlebar px-3 py-1.5"
    >
      <span className="text-xs font-semibold text-cmp-table-titlebar-fg">{title}</span>
      <span className="text-xs text-cmp-table-titlebar-fg opacity-75">Columns · Export</span>
    </div>
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr data-theme-part="Grid column header">
          {columns.map((c) => (
            <th
              key={c}
              className="border-b border-header-edge bg-cmp-table-header px-2 py-1.5 text-left
                         font-semibold text-cmp-table-header-fg"
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, i) => (
          <tr
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            data-theme-part={i === hoverRow ? 'Grid row (hovered)' : 'Grid row'}
            className={`border-b border-hairline ${i === hoverRow ? 'bg-cmp-table-row-hover' : 'bg-panel'}`}
          >
            {cells.map((cell, j) => (
              // eslint-disable-next-line react/no-array-index-key
              <td key={j} className="px-2 py-1.5 text-body">{cell}</td>
            ))}
          </tr>
        ))}
        <tr className="bg-panel">
          <td colSpan={columns.length} className="px-2 py-3 text-center text-muted">
            No further rows match this filter
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const Field: React.FC<{ label: string; value: string; focused?: boolean; readOnly?: boolean }> = ({
  label, value, focused, readOnly,
}) => (
  <div className="flex items-center gap-2">
    <span className="w-1/2 text-right text-xs text-cmp-field-label">{label}</span>
    <input
      readOnly
      value={value}
      data-theme-part={focused ? 'Field (focused)' : readOnly ? 'Field (read-only)' : 'Form field'}
      className={`w-1/2 rounded border px-2 py-1 text-xs
        ${readOnly
          ? 'border-rule bg-sunken text-muted'
          : 'border-cmp-field-edge bg-cmp-field text-cmp-field-fg'}
        ${focused ? 'ring-2 border-accent' : ''}`}
    />
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode; footer?: React.ReactNode }> = ({
  title, children, footer,
}) => (
  <div data-theme-part="Card" className="overflow-hidden rounded border border-rule bg-panel">
    <div className="border-b border-hairline bg-header px-3 py-1.5">
      <span className="text-xs font-semibold text-header-fg">{title}</span>
    </div>
    <div className="p-3">{children}</div>
    {footer && <div className="border-t border-hairline bg-sunken px-3 py-1.5 text-xs text-muted">{footer}</div>}
  </div>
);

/* -------------------------------------------------------------- pro detail */

/**
 * The Order Info tab is the densest screen in the app: four collapsible section
 * panels across the top, then a full-width legs grid. Its controls are ~20px
 * tall with right-aligned labels, so it is the scene that catches a field
 * border, a label colour or a row rhythm that only looks fine at comfortable
 * sizes. Values below are a real export order's shape, retyped.
 */

/** A ~20px form control. `flagged` is the amber cross-field highlight the real
 *  form paints on Pool/Chass and the move flags; `conflict` is the red border a
 *  field gets when it disagrees with a fresh server load (CONFLICT_CLASS). */
const Ctl: React.FC<{
  value?: string;
  placeholder?: string;
  select?: boolean;
  flagged?: boolean;
  conflict?: boolean;
  grow?: boolean;
}> = ({ value, placeholder, select, flagged, conflict, grow }) => (
  <div
    data-theme-part={conflict ? 'Field (conflict)' : flagged ? 'Field (highlighted)' : 'Form field'}
    className={`flex h-5 items-center gap-1 overflow-hidden rounded border px-1
      ${grow ? 'min-w-0 flex-1' : ''}
      ${conflict
        ? 'border-danger bg-cmp-field text-cmp-field-fg'
        : flagged
          ? 'border-flag-edge bg-flag text-flag-fg'
          : 'border-cmp-field-edge bg-cmp-field text-cmp-field-fg'}`}
  >
    <span className="truncate">{value || <span className="text-muted">{placeholder}</span>}</span>
    {select && <span className="ml-auto text-muted">▾</span>}
  </div>
);

/** Label right, control left — the layout every field on this tab uses. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center gap-1.5">
    <span className="w-[38%] shrink-0 truncate text-right text-cmp-field-label">{label}</span>
    <div className="flex min-w-0 flex-1 items-center gap-1">{children}</div>
  </div>
);

/** A value the form renders as text rather than an input, blank as an em dash. */
const Val: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className={children ? 'truncate text-body' : 'text-muted'}>{children || '—'}</span>
);

/** One of the four collapsible panels on the Order Info tab. */
const SectionPanel: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({
  icon, title, children,
}) => (
  <div data-theme-part="Section panel" className="overflow-hidden rounded border border-rule bg-panel">
    <div
      data-theme-part="Section header"
      className="flex items-center gap-1.5 border-b border-header-edge bg-header px-2 py-1"
    >
      <span className="text-accent-text">{icon}</span>
      <span className="font-semibold uppercase tracking-wide text-header-fg">{title}</span>
      <span className="ml-auto text-muted">⌄</span>
    </div>
    <div className="space-y-1 p-2">{children}</div>
  </div>
);

/* -- legs ------------------------------------------------------------------ */

// Mirrors LegsGrid's GRID_TEMPLATE: # | 4 origin cols | 4 destination cols |
// details rail. The real grid also carries a splitter column between the two
// halves for the add/remove-stop affordance.
const LEG_COLS = '24px 1.1fr 0.6fr 0.9fr 1fr 1.1fr 0.6fr 0.9fr 1fr 132px';

/** One cell of the three-row legs header. */
const LegTh: React.FC<{ children?: React.ReactNode; col?: string; row?: string }> = ({ children, col, row }) => (
  <div
    className="truncate border-b border-r border-header-edge bg-cmp-table-header px-1.5 py-0.5
               font-semibold uppercase tracking-wide text-cmp-table-header-fg"
    style={{ gridColumn: col, gridRow: row }}
  >
    {children}
  </div>
);

/** A leg's DETAILS rail: drop mode, live status, allocated miles and driver pay. */
const LegDetails: React.FC<{ drop: string; dropTone: Tone; status?: string; miles: string; pay: string }> = ({
  drop, dropTone, status, miles, pay,
}) => (
  <div className="space-y-0.5 border-l border-hairline px-1.5 py-1">
    <div className="flex items-center justify-between gap-1">
      <span className="text-cmp-field-label">DROP</span>
      <Pill tone={dropTone}>{drop}</Pill>
    </div>
    {status && (
      <div className="flex items-center justify-between gap-1">
        <span className="text-cmp-field-label">STATUS</span>
        <span className="truncate text-body">{status}</span>
      </div>
    )}
    <div className="flex items-center justify-between gap-1">
      <span className="text-cmp-field-label">MILES</span>
      <span className="text-body">{miles}</span>
    </div>
    <div className="flex items-center justify-between gap-1">
      <span className="text-cmp-field-label">PAY</span>
      <span className="font-semibold text-heading">{pay}</span>
    </div>
  </div>
);

interface SceneLeg {
  num: number;
  /** Renders with the accent border the real grid puts on the active leg. */
  selected?: boolean;
  /** The INCLUDED chip that marks a leg rolled into the one above it. */
  included?: boolean;
  origType: string;
  origAppt?: string;
  origTime?: [string, string];
  origName: string;
  /** Red (!) — the location failed its validation check. */
  origWarn?: boolean;
  origAddr: string;
  destType: string;
  destAppt?: string;
  destTime?: [string, string];
  destName: string;
  destWarn?: boolean;
  destAddr: string;
  drop: string;
  dropTone: Tone;
  status?: string;
  miles: string;
  pay: string;
  /** The driver / Suggest strip under the last leg. */
  sendToMove?: 'plain' | 'driver';
}

const LEGS: SceneLeg[] = [
  {
    num: 1,
    origType: 'CHASSIS SPLIT', origAppt: '02/27/2026',
    origName: '42K - FRONTIER LOGISTICS', origWarn: true,
    origAddr: '601 NE LOOP 820 SERVICE RD, FORT WORTH, TX',
    destType: 'PICKUP', destAppt: '02/27/2026',
    destName: 'IMCG - HASLET', destWarn: true,
    destAddr: '12248 US-287 N, HASLET, TX',
    drop: 'Live', dropTone: 'info', status: 'DEST DEPARTED', miles: '12.96', pay: '$49.00',
    included: true,
  },
  {
    num: 2, selected: true,
    origType: 'PICKUP',
    origName: 'IMCG - HASLET',
    origAddr: '12248 US-287 N, HASLET, TX',
    destType: 'DELIVERY', destTime: ['08:00', '16:00'],
    destName: '42K - FRONTIER LOGISTICS',
    destAddr: '601 NE LOOP 820 SERVICE RD, FORT WORTH, TX',
    drop: 'Drop', dropTone: 'ok', miles: '12.96', pay: '$90.65',
    sendToMove: 'plain',
  },
  {
    num: 3,
    origType: 'DELIVERY',
    origName: '42K - FRONTIER LOGISTICS',
    origAddr: '601 NE LOOP 820 SERVICE RD, FORT WORTH, TX',
    destType: 'TERMINATE',
    destName: 'SFA - BNSF - ALLIANCE',
    destAddr: '1111 INTERMODAL PARKWAY, HASLET, TX',
    drop: 'Live', dropTone: 'info', miles: '15.2', pay: '$139.65',
    sendToMove: 'driver',
  },
];

/** The three stacked lines that make up one leg. */
const LegBlock: React.FC<{ leg: SceneLeg }> = ({ leg }) => (
  <div
    data-theme-part={leg.selected ? 'Leg (selected)' : 'Leg'}
    className={`rounded ${leg.selected ? 'border-2 border-accent' : 'border-b border-hairline'}`}
  >
    <div className="grid items-center gap-x-1 gap-y-1 px-1 py-1" style={{ gridTemplateColumns: LEG_COLS }}>
      {/* Line 1 — move type, appt #, appt date, appt window */}
      <div className="row-span-3 text-center text-muted">{leg.num}</div>
      <Ctl value={leg.origType} select />
      <Ctl placeholder="—" />
      <Ctl value={leg.origAppt} placeholder="Date" />
      <div className="flex min-w-0 items-center gap-1">
        <Ctl value={leg.origTime?.[0]} placeholder="HH:MM" grow />
        <span className="text-muted">to</span>
        <Ctl value={leg.origTime?.[1]} placeholder="HH:MM" grow />
      </div>
      <Ctl value={leg.destType} select />
      <Ctl placeholder="—" />
      <Ctl value={leg.destAppt} placeholder="Date" />
      <div className="flex min-w-0 items-center gap-1">
        <Ctl value={leg.destTime?.[0]} placeholder="HH:MM" grow />
        <span className="text-muted">to</span>
        <Ctl value={leg.destTime?.[1]} placeholder="HH:MM" grow />
      </div>
      <div className="row-span-3">
        <LegDetails drop={leg.drop} dropTone={leg.dropTone} status={leg.status} miles={leg.miles} pay={leg.pay} />
      </div>

      {/* Line 2 — location typeahead, arrival, departure */}
      <div className="col-span-2 flex min-w-0 items-center gap-1">
        <span className="text-muted">⌕</span>
        <Ctl value={leg.origName} grow />
        {leg.origWarn && <span data-theme-part="Field warning" className="text-danger">!</span>}
      </div>
      <Ctl placeholder="Date/Time" />
      <Ctl placeholder="Date/Time" />
      <div className="col-span-2 flex min-w-0 items-center gap-1">
        <span className="text-muted">⌕</span>
        <Ctl value={leg.destName} grow />
        {leg.destWarn && <span data-theme-part="Field warning" className="text-danger">!</span>}
      </div>
      <Ctl placeholder="Date/Time" />
      <Ctl placeholder="Date/Time" />

      {/* Line 3 — resolved address (a link to the location) and the stop memo */}
      <div className="col-span-2 truncate text-accent-text">{leg.origAddr}</div>
      <div className="col-span-2"><Ctl placeholder="memo..." /></div>
      <div className="col-span-2 truncate text-accent-text">{leg.destAddr}</div>
      <div className="col-span-2"><Ctl placeholder="memo..." /></div>
    </div>

    {(leg.included || leg.sendToMove) && (
      <div className="flex items-center gap-2 px-2 pb-1">
        {leg.included && (
          <span
            data-theme-part="Leg chip"
            className="rounded border border-rule bg-sunken px-1.5 py-0.5 text-muted"
          >
            INCLUDED
          </span>
        )}
        <span className="text-muted">ⓘ</span>
        <span className="text-muted">▷</span>
        {leg.sendToMove && (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-cmp-field-label">SEND TO MOVE</span>
            <span data-theme-part="Toggle (off)" className="h-3 w-6 rounded-full bg-rule-strong" />
            {leg.sendToMove === 'driver' ? (
              <>
                <span className="text-body">Driver</span>
                <GhostButton>Suggest</GhostButton>
                <Ctl value="000" />
              </>
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

/* --------------------------------------------------------- at risk monitor */

/**
 * At Risk Monitor leads with three tinted summary tiles and then a very wide
 * grid. It is the scene where the tone tints have to work as large flat areas
 * rather than as pills, and where the grid's per-row risk stripe, header, filter
 * row and dark toolbar all have to stay separable from one another.
 */

/** One of the three headline tiles. The tone tint is the whole tile. */
const StatusTile: React.FC<{ tone: Tone; label: string; sub: string; value: string }> = ({
  tone, label, sub, value,
}) => (
  <div
    data-theme-part={`Status tile (${tone})`}
    className={`flex items-center justify-between gap-3 px-4 py-2 ${TONE_CLASS[tone]}`}
  >
    <span>
      <span className="block text-sm font-semibold">{label}</span>
      <span className="block opacity-75">{sub}</span>
    </span>
    <span className="text-2xl font-semibold">{value}</span>
  </div>
);

const STAT_VALUE: Record<'plain' | 'warn' | 'accent', string> = {
  plain: 'font-semibold text-heading',
  warn: 'font-semibold text-warn-fg',
  accent: 'font-semibold text-accent-text',
};

/** A cell in the two secondary count rows under the tiles. */
const Stat: React.FC<{ label: string; value: string; emphasis?: 'warn' | 'accent' }> = ({
  label, value, emphasis,
}) => (
  <div className="flex items-center justify-between gap-2 border-r border-hairline bg-panel px-3 py-1.5">
    <span className="truncate text-body">{label}</span>
    <span className={STAT_VALUE[emphasis ?? 'plain']}>{value}</span>
  </div>
);

/** The saturated left edge each grid row carries, keyed to its risk. */
const STRIPE: Record<Tone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  flag: 'bg-flag',
};

/** A small square count badge — the SF / TT columns. */
const Sq: React.FC<{ n: string; tone: Tone }> = ({ n, tone }) => (
  <span data-theme-part={`Count square (${tone})`} className={`rounded px-1 font-medium ${TONE_CLASS[tone]}`}>{n}</span>
);

/** Origin and destination render as a code over a muted city. */
const Place: React.FC<{ code: string; city: string }> = ({ code, city }) => (
  <span>
    <span className="block text-body">{code}</span>
    <span className="block text-muted">{city}</span>
  </span>
);

interface RiskRow {
  stripe: Tone;
  risk: 'Critical' | 'On Track';
  pro: string;
  sf?: string;
  tt?: string;
  eta: string;
  req?: string;
  cu?: string;
  subreason?: string;
  /** Renders the hovered cell's tooltip, the way the real grid truncates. */
  tip?: string;
  dispatcher: string;
  driver: string;
  moveType: string;
  scac: string;
  customer: string;
  origCode: string;
  origCity: string;
}

const RISK_TONE: Record<RiskRow['risk'], Tone> = { Critical: 'danger', 'On Track': 'ok' };

const RISK_ROWS: RiskRow[] = [
  { stripe: 'danger', risk: 'Critical', pro: '5094738-1', eta: '-', req: '93K', dispatcher: 'Devin Fitch', driver: '-', moveType: 'EXPORT', scac: 'MAEU', customer: 'TRICON ENER…', origCode: 'BPT', origCity: 'SEABROOK, TX' },
  { stripe: 'ok', risk: 'Critical', pro: '5102333-1', eta: '19:40', req: '🔒', dispatcher: 'Brandy Conroy', driver: '2873', moveType: 'EXPORT', scac: 'MAEU', customer: 'KAY POLYMER…', origCode: 'BPT', origCity: 'SEABROOK, TX' },
  { stripe: 'danger', risk: 'On Track', pro: '5091362-1', eta: '20:27', dispatcher: 'Keven Reyna', driver: '5811', moveType: 'INBOUND', scac: '-', customer: 'GOODYEAR TI…', origCode: 'GWI31', origCity: 'LA PORTE, TX' },
  { stripe: 'danger', risk: 'On Track', pro: '5091368-1', eta: '20:27', dispatcher: 'Keven Reyna', driver: '6465', moveType: 'INBOUND', scac: '-', customer: 'GOODYEAR TI…', origCode: 'GWI31', origCity: 'LA PORTE, TX' },
  { stripe: 'ok', risk: 'On Track', pro: '5098820-1', eta: '20:43', dispatcher: 'CY Houston', driver: '3653', moveType: 'EXPORT', scac: 'HLCU', customer: 'TALKE USA INC', origCode: 'BCT', origCity: 'LA PORTE, TX' },
  { stripe: 'warn', risk: 'On Track', pro: '5060108-1', sf: '1', tt: '1', eta: '19:14', cu: 'U', subreason: 'NO FAILURE - EXPE…', dispatcher: 'Bayport', driver: '6825', moveType: 'EXPORT', scac: 'EVRU', customer: 'DOMTAR - AS…', origCode: 'BCT', origCity: 'LA PORTE, TX' },
  { stripe: 'warn', risk: 'On Track', pro: '5102330-1', eta: '-', subreason: 'TERMINAL/CY CON…', tip: 'TERMINAL/CY CONGESTION', dispatcher: "Barbour's Cut", driver: '-', moveType: 'EXPORT', scac: 'HLCU', customer: 'CATERPILLAR …', origCode: 'BCT', origCity: 'LA PORTE, TX' },
];

const RISK_COLS = ['Risk', 'PRO#', 'SF', 'TT', 'ETA', 'Req', 'C/U', 'Subreason', 'Dispatcher', 'Driver', 'Move Type', 'SCAC', 'Customer', 'Origin'];

const TOOL_BTN: Record<'on' | 'off', string> = {
  on: 'rounded bg-accent px-1.5 py-0.5 text-accent-fg',
  off: 'rounded px-1.5 py-0.5 text-cmp-table-titlebar-fg opacity-75',
};

/** The presence row at the foot of the page — who else is on this board. */
const AVATAR_TONES: Tone[] = ['ok', 'info', 'warn', 'flag', 'danger'];

/* ------------------------------------------------------------ lc dashboard */

/**
 * LC Dashboards is a wall of small count panels. Almost every value is a zero in
 * quiet text, and the few that are not carry a heat badge — so this scene is the
 * one that shows whether the tone colours still read as a ramp (attention →
 * urgent) against the panel surface, and whether a dark panel header still has
 * contrast once the surfaces move.
 */
type Heat = 'cool' | 'warm' | 'hot';

const HEAT_CLASS: Record<Heat, string> = {
  cool: 'bg-flag text-flag-fg',
  warm: 'bg-warn text-warn-fg',
  hot: 'bg-danger text-danger-fg',
};

const ROW_BG: Record<'plain' | 'active', string> = {
  plain: 'bg-panel',
  active: 'bg-highlight',
};

const LABEL_CLASS: Record<'flush' | 'indent', string> = {
  flush: 'truncate text-body',
  indent: 'truncate pl-3 text-body',
};

/** A count. Zero stays quiet; the ones that need attention get a heat badge. */
const Count: React.FC<{ n: string; heat?: Heat }> = ({ n, heat }) =>
  heat ? (
    <span
      data-theme-part={`Count badge (${heat})`}
      className={`shrink-0 rounded px-1.5 font-medium ${HEAT_CLASS[heat]}`}
    >
      {n}
    </span>
  ) : (
    <span className={n === '0' ? 'shrink-0 text-muted' : 'shrink-0 text-body'}>{n}</span>
  );

/** A panel: dark header with a collapse chevron, centred title and detach icon. */
const DashPanel: React.FC<{ title: string; collapsed?: boolean; children?: React.ReactNode }> = ({
  title, collapsed, children,
}) => (
  <div data-theme-part="Dashboard panel" className="overflow-hidden rounded border border-rule bg-panel">
    <div data-theme-part="Panel header" className="flex items-center gap-2 bg-cmp-table-titlebar px-2 py-1">
      <span className="text-cmp-table-titlebar-fg opacity-75">{collapsed ? '⌄' : '⌃'}</span>
      <span className="flex-1 text-center font-semibold text-cmp-table-titlebar-fg">{title}</span>
      <span className="text-cmp-table-titlebar-fg opacity-75">⧉</span>
    </div>
    {!collapsed && children}
  </div>
);

interface DashCell {
  label: string;
  n: string;
  heat?: Heat;
  indent?: boolean;
  /** The row the user has clicked into — the dashboard's selected-row treatment. */
  active?: boolean;
}

/** The label/count rows a panel is built from, laid out in one or two columns. */
const DashRows: React.FC<{ cols: number; cells: (DashCell | null)[] }> = ({ cols, cells }) => (
  <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
    {cells.map((c, i) => (
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        data-theme-part={c?.active ? 'Row (selected)' : undefined}
        className={`flex items-center justify-between gap-2 border-b border-hairline px-2 py-1
                    ${ROW_BG[c?.active ? 'active' : 'plain']}`}
      >
        {c && (
          <>
            <span className={LABEL_CLASS[c.indent ? 'indent' : 'flush']}>{c.label}</span>
            <Count n={c.n} heat={c.heat} />
          </>
        )}
      </div>
    ))}
  </div>
);

/** The header strip some panels repeat inside the body, with extra count columns. */
const DashSubHeader: React.FC<{ name: string; cols?: string[] }> = ({ name, cols = [] }) => (
  <div
    data-theme-part="Panel column header"
    className="grid bg-cmp-table-header font-semibold text-cmp-table-header-fg"
    style={{ gridTemplateColumns: `1.6fr repeat(${Math.max(cols.length, 1)}, minmax(0, 1fr))` }}
  >
    <span className="truncate px-2 py-1">{name}</span>
    {(cols.length ? cols : ['0']).map((c) => (
      <span key={c} className="px-2 py-1 text-right">{c}</span>
    ))}
  </div>
);

interface MatrixRow {
  label: string;
  /** A date-range subtotal line, heavier than the rows under it. */
  group?: boolean;
  cells: { n: string; heat?: Heat }[];
}

const MATRIX_ROW: Record<'group' | 'plain', string> = {
  group: 'grid border-b border-hairline bg-sunken font-semibold text-heading',
  plain: 'grid border-b border-hairline bg-panel text-body',
};

/** Tracking / Delivery Schedule: a label column then a heat-mapped number grid. */
const Matrix: React.FC<{ name: string; head: string[]; rows: MatrixRow[] }> = ({ name, head, rows }) => {
  const cols = `1.5fr repeat(${head.length}, minmax(0, 1fr))`;
  return (
    <div>
      <div
        data-theme-part="Panel column header"
        className="grid bg-cmp-table-header font-semibold text-cmp-table-header-fg"
        style={{ gridTemplateColumns: cols }}
      >
        <span className="truncate px-2 py-1">{name}</span>
        {head.map((h) => <span key={h} className="px-1 py-1 text-right">{h}</span>)}
      </div>
      {rows.map((r) => (
        <div key={r.label} className={MATRIX_ROW[r.group ? 'group' : 'plain']} style={{ gridTemplateColumns: cols }}>
          <span className="truncate px-2 py-1">{r.label}</span>
          {r.cells.map((c, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className="px-1 py-1 text-right"><Count n={c.n} heat={c.heat} /></span>
          ))}
        </div>
      ))}
    </div>
  );
};

/** One of the three stacked lists inside the Pending Orders panel. */
const PendingCol: React.FC<{ title: string; total: string; rows: DashCell[] }> = ({ title, total, rows }) => (
  <div className="border-r border-hairline">
    <div className="flex items-center justify-between gap-2 bg-cmp-table-header px-2 py-1 font-semibold text-cmp-table-header-fg">
      <span className="truncate">{title}</span>
      <span>{total}</span>
    </div>
    {rows.map((r) => (
      <div
        key={r.label}
        data-theme-part={r.active ? 'Row (selected)' : undefined}
        className={`flex items-center justify-between gap-2 border-b border-hairline px-2 py-1
                    ${ROW_BG[r.active ? 'active' : 'plain']}`}
      >
        <span className={LABEL_CLASS.flush}>{r.label}</span>
        <Count n={r.n} />
      </div>
    ))}
  </div>
);

const WS_TAB: Record<'on' | 'off', string> = {
  on: 'flex items-center gap-1.5 rounded-t border border-rule border-b-transparent bg-panel px-2 py-1 font-semibold text-cmp-tab-active-fg',
  off: 'flex items-center gap-1.5 rounded-t px-2 py-1 text-cmp-tab-fg',
};

const WS_BADGE: Record<'on' | 'off', string> = {
  on: 'rounded bg-accent px-1 text-accent-fg',
  off: 'rounded bg-highlight px-1 text-muted',
};

/* ------------------------------------------------------------------ scenes */

const atRiskMonitor = () => (
  <div data-theme-part="Page" className="bg-app text-xs">
    <FilterRow filters={[
      'Order Dispatcher', 'Driver Dispatcher', 'All Regions', 'All Order LCs',
      'All Sales Reps', 'All Customers', 'All Key Statuses',
    ]} />

    {/* Three headline tiles, then two rows of secondary counts. */}
    <div className="grid grid-cols-3 border-b border-rule">
      <StatusTile tone="danger" label="Critical" sub="Will be late" value="839" />
      <StatusTile tone="warn" label="At Risk" sub="Less than 30 min buffer" value="0" />
      <StatusTile tone="ok" label="On Track" sub="30+ min buffer" value="288" />
    </div>
    <div className="grid grid-cols-5 border-b border-hairline">
      <Stat label="Not Dispatched" value="785" emphasis="warn" />
      <Stat label="Not Started" value="218" />
      <Stat label="At Origin" value="12" />
      <Stat label="In Transit" value="113" />
      <Stat label="At Destination" value="15" />
    </div>
    <div className="grid grid-cols-5 border-b border-rule">
      <Stat label="Hyper Focus" value="434" emphasis="accent" />
      <Stat label="LFD" value="155" />
      <Stat label="Per Diem" value="798" />
      <Stat label="Cutoff" value="11" />
      <Stat label="Appointment" value="178" />
    </div>

    <div data-theme-part="Data grid" className="m-2 overflow-hidden rounded border border-rule">
      {/* Dark toolbar: count, load time, search, and the view/format buttons —
          two of which are toggled on and take the accent. */}
      <div data-theme-part="Grid title bar" className="flex items-center gap-2 bg-cmp-table-titlebar px-3 py-1.5">
        <span className="font-semibold text-cmp-table-titlebar-fg">At Risk Monitor</span>
        <span className="text-cmp-table-titlebar-fg opacity-75">(1143)</span>
        <span className="text-cmp-table-titlebar-fg opacity-75">Loaded in 0:01</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span
            data-theme-part="Search input"
            className="flex h-6 w-40 items-center rounded border border-cmp-field-edge bg-cmp-field px-2 text-muted"
          >
            Search moves...
          </span>
          {[['▦', 'off'], ['⧩', 'on'], ['Σ', 'off'], ['☰', 'on'], ['⇄', 'off'], ['⤓', 'off']].map(([icon, state], i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className={TOOL_BTN[state as 'on' | 'off']}>{icon}</span>
          ))}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[62rem] border-collapse">
          <thead>
            <tr data-theme-part="Grid column header">
              <th className="w-1 bg-cmp-table-header p-0" />
              {RISK_COLS.map((c) => (
                <th
                  key={c}
                  className="whitespace-nowrap border-b border-header-edge bg-cmp-table-header px-2 py-1
                             text-left font-semibold text-cmp-table-header-fg"
                >
                  {c} <span className="opacity-50">⇅</span>
                </th>
              ))}
            </tr>
            {/* Per-column filter row — its own band under the headers. */}
            <tr data-theme-part="Grid filter row">
              <th className="w-1 bg-sunken p-0" />
              {RISK_COLS.map((c) => (
                <th key={c} className="border-b border-hairline bg-sunken px-1 py-1">
                  <span className="flex h-5 items-center rounded border border-cmp-field-edge bg-cmp-field px-1 text-muted">
                    Filter...
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RISK_ROWS.map((r) => (
              <tr key={r.pro} data-theme-part="Grid row" className="border-b border-hairline bg-panel">
                <td data-theme-part={`Risk stripe (${r.stripe})`} className={`w-1 p-0 ${STRIPE[r.stripe]}`} />
                <td className="px-2 py-1"><Pill tone={RISK_TONE[r.risk]}>{r.risk}</Pill></td>
                <td className="whitespace-nowrap px-2 py-1">
                  <a href="#preview" className="text-accent-text">{r.pro}</a>
                </td>
                <td className="px-2 py-1">{r.sf ? <Sq n={r.sf} tone="warn" /> : ''}</td>
                <td className="px-2 py-1">{r.tt ? <Sq n={r.tt} tone="danger" /> : ''}</td>
                <td className="whitespace-nowrap px-2 py-1 text-body">{r.eta}</td>
                <td className="px-2 py-1 text-muted">{r.req}</td>
                <td className="px-2 py-1 text-body">{r.cu}</td>
                {/* The truncated cell the user is pointing at, with its tooltip. */}
                <td className="relative px-2 py-1 text-body">
                  <span className="block max-w-[9rem] truncate">{r.subreason}</span>
                  {r.tip && (
                    <span
                      data-theme-part="Tooltip"
                      className="absolute bottom-full left-0 z-10 whitespace-nowrap rounded border border-rule
                                 bg-overlay px-2 py-1 text-body shadow-lg"
                    >
                      {r.tip}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-2 py-1 text-body">{r.dispatcher}</td>
                <td className="px-2 py-1">
                  {r.driver === '-' ? <span className="text-muted">-</span> : <a href="#preview" className="text-accent-text">{r.driver}</a>}
                </td>
                <td className="px-2 py-1"><Pill tone="info">{r.moveType}</Pill></td>
                <td className="px-2 py-1 text-body">{r.scac}</td>
                <td className="whitespace-nowrap px-2 py-1 text-body">{r.customer}</td>
                <td className="whitespace-nowrap px-2 py-1"><Place code={r.origCode} city={r.origCity} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Presence — who else has this board open. */}
    <div data-theme-part="Presence bar" className="flex items-center gap-1 border-t border-rule bg-chrome px-3 py-1.5">
      <span className="text-muted">👥</span>
      {['ES', 'JS', 'AB', 'LM', 'KB', 'VD', 'EG', 'AG', 'JM', 'KS'].map((initials, i) => (
        <span
          key={initials}
          data-theme-part="Avatar"
          className={`flex h-5 w-5 items-center justify-center rounded-full ${TONE_CLASS[AVATAR_TONES[i % AVATAR_TONES.length]]}`}
        >
          {initials}
        </span>
      ))}
      <span className="flex h-5 items-center rounded-full bg-highlight px-1.5 text-muted">+37</span>
      <a href="#preview" className="ml-auto text-muted">Logic Breakdown</a>
    </div>
  </div>
);

const proDetail = () => (
  <div data-theme-part="Page" className="bg-app text-xs">
    {/* Order header strip — key/value pairs across one line, not a title bar.
        The recno beside each name is the muted half of the pair. */}
    <div
      data-theme-part="Order header"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-cmp-page-header-edge
                 bg-cmp-page-header px-3 py-2 text-cmp-page-header-fg"
    >
      <span className="opacity-75">PRO #</span>
      <span className="font-semibold">4904696</span>
      <span className="opacity-75">BILL TO</span>
      <span>CHEVRON PHILLIPS CHEMICAL COMPANY LP</span>
      <span className="opacity-60">4684</span>
      <span className="opacity-75">CUSTOMER</span>
      <span>CHEVRON PHILLIPS - DFW UL</span>
      <span className="opacity-60">6732</span>
      <Pill tone="info">EXPORT</Pill>
      <span className="opacity-75">MARGIN</span>
      <Pill tone="ok">60.6%</Pill>
      <span className="ml-auto flex items-center gap-2">
        <span className="opacity-75">MASTER PRO</span>
        <span className="font-semibold">4904696</span>
        <span className="opacity-75">1 of 3</span>
        <span className="opacity-75">›</span>
        <span className="opacity-75">✕</span>
      </span>
    </div>

    {/* PRO_TABS, with their access keys and unread counts. */}
    <div data-theme-part="Tab bar" className="flex items-center gap-1 overflow-x-auto border-b border-rule bg-sunken px-2">
      {[
        ['O', 'Order Info', ''], ['N', 'Addl Info', ''], ['T', 'Tracing', ''], ['W', 'Whse', ''],
        ['V', 'Addl Cost', ''], ['X', 'Invoicing', ''], ['Z', 'Docs', '3'], ['G', 'Notes', '1'],
        ['H', 'History', ''],
      ].map(([key, label, count], i) => (
        <button
          key={label}
          type="button"
          data-theme-part={i === 0 ? 'Tab (active)' : 'Tab'}
          className={`flex items-center gap-1 whitespace-nowrap rounded-t border-b-2 px-2 py-1.5 ${i === 0
            ? 'border-cmp-tab-edge bg-panel font-semibold text-cmp-tab-active-fg'
            : 'border-transparent text-cmp-tab-fg'}`}
        >
          <span className="text-muted">[{key}]</span>
          {label}
          {count && <span className="rounded-full bg-info px-1.5 text-info-fg">{count}</span>}
        </button>
      ))}
      <span className="ml-auto text-muted">🔖</span>
    </div>

    {/* The four section panels. They sit side by side at full width and stack
        down as the window narrows — the preview panel is usually narrow. */}
    <div className="grid gap-2 p-2 md:grid-cols-2 xl:grid-cols-4">
      <SectionPanel icon="👤" title="People & Equipment">
        <Row label="CSR"><Ctl value="Kendall Scott" select grow /></Row>
        <Row label="Whse CSR"><Ctl placeholder="—" select grow /></Row>
        <Row label="Dispatcher"><Ctl value="DFW-Local" select grow /></Row>
        <Row label="CST Dispatcher"><Ctl placeholder="—" select grow /></Row>
        <Row label="Carrier"><Val /></Row>
        <Row label="Size / Type">
          <Ctl value="40" select />
          <Ctl value="HIGHCUBE" select grow />
        </Row>
        <Row label="Equipment # / Flex">
          <Ctl grow />
          <Ctl value="Y" select />
        </Row>
        <Row label="Pool / Chass">
          <Ctl value="GWI" select flagged />
          <Ctl value="ULTRA LIGHT" select flagged grow />
        </Row>
        <Row label="Chass #"><Ctl grow /><span className="text-warn">✎</span></Row>
        <Row label="Booking"><Ctl value="267164420" grow /></Row>
        <Row label="SCAC">
          <Ctl value="MAEU — MAERSK" select grow />
          <span className="text-accent-text">➔</span>
          <span className="text-accent-text">ⓘ</span>
        </Row>
      </SectionPanel>

      <SectionPanel icon="🏳" title="Move Flags & Schedule">
        {/* The Y/N flags run in two columns; Daycab and the Swing/Drop pair are
            carrying the amber cross-field highlight. */}
        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
          <Row label="Daycab"><Ctl value="Y" select flagged /></Row>
          <Row label="Scale"><Ctl value="N" select /></Row>
          <Row label="OTR"><Ctl value="N" select /></Row>
          <Row label="Tank"><Ctl value="N" select /></Row>
          <Row label="Hot"><Ctl value="N" select /></Row>
          <Row label="Swing"><Ctl value="Y" select flagged /></Row>
          <Row label="Haz"><Ctl value="N" select /></Row>
          <Row label="Drop"><Ctl value="Y" select flagged /></Row>
          <Row label="Railbill"><Ctl value="N" select /></Row>
        </div>
        <Row label="Terminal">
          <Ctl value="BNSF" select grow />
          <span className="text-accent-text">➔</span>
        </Row>
        <Row label="1st Rcv"><Val /></Row>
        <Row label="Doc Cut / Cust"><Val /><Ctl value="N" select /></Row>
        <Row label="Cutoff / Cust"><Val /><Ctl value="N" select /></Row>
        <Row label="Bkg Hold / Fault">
          <Ctl value="ROLLING" select />
          <Ctl value="CUSTOMER" select grow />
        </Row>
        <Row label="Available"><Val /></Row>
        <Row label="Avail To Ship">
          <span data-theme-part="Checkbox" className="h-3 w-3 rounded-sm border border-cmp-field-edge bg-cmp-field" />
          <Val /><Val />
        </Row>
        <Row label="Target Deliv"><Ctl value="ON" select /><Ctl placeholder="Target" grow /></Row>
        <Row label="Cust Target"><Ctl placeholder="Cust Target" grow /></Row>
        <Row label="Drop Rel"><Ctl placeholder="Date/Time" grow /></Row>
        <Row label="Memo">
          <div
            data-theme-part="Textarea"
            className="h-12 min-w-0 flex-1 rounded border border-cmp-field-edge bg-cmp-field px-1 py-0.5 text-muted"
          >
            Memo
          </div>
        </Row>
      </SectionPanel>

      <SectionPanel icon="📍" title="Locations & Routing">
        <Row label="Piece Count"><Ctl value="990" grow /></Row>
        <Row label="Commodity"><Ctl value="MARLEX D139-P01 POLYETHYLENE IN BAG" grow /></Row>
        <Row label="PO"><Ctl value="9497274A" grow /></Row>
        {/* The weight trio disagrees with the last server load. */}
        <Row label="Lbs / Kgs / MT">
          <Ctl value="54564.3" conflict grow />
          <Ctl value="24750" conflict grow />
          <Ctl value="24.75" conflict grow />
        </Row>
        <Row label="Tare Lbs"><Ctl value="0" /></Row>
        <Row label="Seal No."><Ctl grow /></Row>
        <Row label="Ves / Voy"><Ctl value="POTOMAC EXPRESS" grow /><Ctl value="611W" /></Row>
        <Row label="Port of Orig"><span className="text-muted">⌕</span><Ctl value="LOS ANGELES" grow /></Row>
        <Row label="Port of Disch"><span className="text-muted">⌕</span><Ctl value="SINGAPORE" grow /></Row>
        <Row label="PIN"><Val /><span className="text-cmp-field-label">Chass</span><Val /></Row>
        <Row label="Empty PIN"><Val /><span className="text-cmp-field-label">Empty Chass</span><Val /></Row>
        <Row label="Consignee"><Val>42K - FRONTIER LOGISTICS</Val><span className="ml-auto text-accent-text">✎</span></Row>
        <Row label="Broker"><Val>DAMCO USA INC</Val><span className="ml-auto text-accent-text">✎</span></Row>
        <Row label="Broker Contact"><Val>NEHAL ALIMUDDIN</Val></Row>
        <Row label="Shipper"><Val /></Row>
      </SectionPanel>

      <SectionPanel icon="💬" title="Comments & Trouble Tickets">
        {/* A tab strip inside a panel — a second, smaller tab treatment. */}
        <div className="flex items-center gap-1 border-b border-hairline pb-1">
          {[['COMMENTS', ''], ['TTS', '1'], ['SFS', '7'], ['ROS', '']].map(([label, count], i) => (
            <span
              key={label}
              data-theme-part={i === 0 ? 'Inner tab (active)' : 'Inner tab'}
              className={`flex items-center gap-1 rounded px-1.5 py-0.5 ${i === 0
                ? 'bg-highlight font-semibold text-heading'
                : 'text-muted'}`}
            >
              {label}
              {count && <span className="rounded-full bg-info px-1 text-info-fg">{count}</span>}
            </span>
          ))}
        </div>
        <div className="text-cmp-field-label">COMMENTS</div>
        <div
          data-theme-part="Textarea"
          className="h-20 rounded border border-cmp-field-edge bg-cmp-field px-1.5 py-1 text-cmp-field-fg"
        >
          ***WEIGHT 54,564.3 LBS***
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 pt-1">
          <Row label="CST Booking #"><Ctl grow /></Row>
          <Row label="Released as Set"><Ctl value="N" select /></Row>
          <Row label="Food Grade"><Ctl value="N" select /></Row>
          <Row label="Off Hire"><Ctl value="N" select /></Row>
          <Row label="Equip Reposition"><Ctl value="N" select /></Row>
          <Row label="Service Failures"><Pill tone="danger">7</Pill></Row>
        </div>
        <Row label="Per Diem"><Val /></Row>
        <a href="#preview" className="text-accent-text">Grounded Info →</a>
      </SectionPanel>
    </div>

    {/* Legs — full width under the panels. */}
    <div data-theme-part="Legs grid" className="mx-2 mb-2 overflow-hidden rounded border border-rule bg-panel">
      <div data-theme-part="Grid title bar" className="flex items-center gap-1.5 bg-cmp-table-titlebar px-3 py-1.5">
        <span className="text-cmp-table-titlebar-fg opacity-75">▤</span>
        <span className="font-semibold uppercase tracking-wide text-cmp-table-titlebar-fg">Legs</span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[52rem]">
          {/* Three header rows: appointment, stop, then address / memo. */}
          <div className="grid" style={{ gridTemplateColumns: LEG_COLS }}>
            <LegTh col="1" row="1 / span 3" />
            <LegTh>Move Type</LegTh>
            <LegTh>Appt #</LegTh>
            <LegTh>Appt Date</LegTh>
            <LegTh>Time</LegTh>
            <LegTh>Move Type</LegTh>
            <LegTh>Appt #</LegTh>
            <LegTh>Appt Date</LegTh>
            <LegTh>Time</LegTh>
            <LegTh col="10" row="1 / span 3">Details</LegTh>

            <LegTh col="2 / span 2">Origin</LegTh>
            <LegTh>Orig Arrival</LegTh>
            <LegTh>Orig Departure</LegTh>
            <LegTh col="6 / span 2">Destination</LegTh>
            <LegTh>Dest Arrival</LegTh>
            <LegTh>Dest Departure</LegTh>

            <LegTh col="2 / span 2">Address</LegTh>
            <LegTh col="4 / span 2">Orig Memo</LegTh>
            <LegTh col="6 / span 2">Address</LegTh>
            <LegTh col="8 / span 2">Dest Memo</LegTh>
          </div>

          <div className="bg-sunken py-1 text-center">
            <a href="#preview" className="text-accent-text">+ Add Leg</a>
          </div>

          {LEGS.map((leg) => <LegBlock key={leg.num} leg={leg} />)}
        </div>
      </div>
    </div>

    {/* Action bar — the page's buttons are all accent except the inert save. */}
    <div
      data-theme-part="Page footer"
      className="flex items-center gap-2 border-t border-rule bg-chrome px-3 py-2"
    >
      <span className="text-muted">2280px</span>
      <div className="ml-auto flex items-center gap-2">
        <PrimaryButton>Send Rail Billing</PrimaryButton>
        <PrimaryButton>LOADED ▾</PrimaryButton>
        <PrimaryButton>Dupe</PrimaryButton>
        <PrimaryButton>[c] Close</PrimaryButton>
        <GhostButton disabled>[s] Save Changes</GhostButton>
      </div>
    </div>
  </div>
);

const dashboard = () => (
  <div data-theme-part="Page" className="bg-app text-xs">
    {/* Saved workspaces. The last one is being renamed, so it carries the
        edit/close pair; the + opens a new workspace. */}
    <div data-theme-part="Workspace tabs" className="flex items-center gap-1 border-b border-rule bg-sunken px-2 pt-1.5">
      {[['1', 'Main'], ['2', 'Exports'], ['3', 'Accounting'], ['4', 'Workspace 4']].map(([n, label], i) => (
        <span key={label} data-theme-part={i === 0 ? 'Tab (active)' : 'Tab'} className={WS_TAB[i === 0 ? 'on' : 'off']}>
          <span className={WS_BADGE[i === 0 ? 'on' : 'off']}>{n}</span>
          {label}
          {i === 3 && (
            <>
              <span className="text-muted">✎</span>
              <span className="text-muted">✕</span>
            </>
          )}
        </span>
      ))}
      <span className="rounded border border-rule bg-panel px-1.5 py-1 text-accent-text">+</span>
      <span className="ml-auto flex items-center gap-2 pb-1">
        <PrimaryButton>+ New Order</PrimaryButton>
        <span className="text-muted">⌃</span>
        <span className="text-muted">⌄</span>
        <span className="text-muted">🗑</span>
        <span className="text-muted">☰</span>
      </span>
    </div>

    <div data-theme-part="Filter bar" className="flex flex-wrap items-center gap-2 border-b border-rule bg-panel px-2 py-1.5">
      <span className="text-cmp-field-label">Filters:</span>
      {['All LCs', 'All Customers', 'All Billing Parties', 'All Key Accounts', 'All Dispatch Regions', 'All Warehouses'].map((f) => (
        <span
          key={f}
          data-theme-part="Form field"
          className="flex h-6 items-center gap-2 rounded border border-cmp-field-edge bg-cmp-field px-2 text-cmp-field-fg"
        >
          {f}<span className="text-muted">⌄</span>
        </span>
      ))}
      <span className="ml-1 text-body">Drawer view</span>
      <span data-theme-part="Toggle (on)" className="flex h-4 w-8 items-center rounded-full bg-accent px-0.5">
        <span className="ml-auto h-3 w-3 rounded-full bg-panel" />
      </span>
    </div>

    <div className="grid gap-2 p-2 lg:grid-cols-2 xl:grid-cols-3">
      {/* -------------------------------------------------------- column 1 */}
      <div className="space-y-2">
        <DashPanel title="Missing Line Item Total">
          <DashSubHeader name="Missing Line Item Total" />
          <DashRows cols={1} cells={[
            { label: 'Bobtail', n: '0' },
            { label: 'Chassis Split', n: '0' },
            { label: 'Grounded', n: '0' },
            { label: 'Waiting Time', n: '0' },
          ]} />
        </DashPanel>

        <DashPanel title="Street Turn">
          <DashRows cols={2} cells={[
            { label: 'Request Not Sent', n: '0' }, { label: 'Approved', n: '0' },
            { label: 'Pending Approval', n: '0' }, { label: 'Cancelled Requests', n: '0' },
            { label: 'Rejected', n: '0' }, { label: 'Cannot Street Turn', n: '0' },
          ]} />
        </DashPanel>

        <DashPanel title="Problem Resolution - Order Entry">
          <DashRows cols={2} cells={[
            { label: 'No LFD', n: '0' }, { label: 'No Per Diem', n: '0' },
            { label: 'LFD ≤ Today, Not Scheduled f…', n: '0' }, { label: 'Booking Not On File', n: '0' },
            { label: 'No Pickup Number', n: '0' }, null,
          ]} />
        </DashPanel>
      </div>

      {/* -------------------------------------------------------- column 2 */}
      <div className="space-y-2">
        <DashPanel title="Chassis Charges">
          <DashSubHeader name="Chassis Charges" cols={['Drop', 'Rel.']} />
          <div className="grid" style={{ gridTemplateColumns: '1.6fr repeat(2, minmax(0, 1fr))' }}>
            {[['GWI', '0', '0'], ['Customer', '265', '45']].flatMap(([label, drop, rel]) => [
              <span key={`${label}l`} className="border-b border-hairline bg-panel px-2 py-1 text-body">{label}</span>,
              <span key={`${label}d`} className="border-b border-hairline bg-panel px-2 py-1 text-right"><Count n={drop} /></span>,
              <span key={`${label}r`} className="border-b border-hairline bg-panel px-2 py-1 text-right"><Count n={rel} /></span>,
            ])}
          </div>
        </DashPanel>

        <DashPanel title="Dropped Containers">
          <DashRows cols={2} cells={[
            { label: 'Dropped', n: '0' }, { label: 'Drop Released', n: '0' },
            { label: 'GWI', n: '0', indent: true }, null,
            { label: 'After Delivery', n: '0', indent: true }, null,
            { label: 'Customer', n: '0', indent: true }, null,
          ]} />
        </DashPanel>

        <DashPanel title="Tickets">
          <DashRows cols={2} cells={[
            { label: 'Driver', n: '18', heat: 'hot' }, { label: 'Pending', n: '410', heat: 'hot' },
            { label: 'New', n: '157', heat: 'cool' }, null,
          ]} />
        </DashPanel>

        {/* A panel the user has collapsed — the header alone, no body. */}
        <DashPanel title="Warehouse Photos (0)" collapsed />
      </div>

      {/* -------------------------------------------------------- column 3 */}
      <div className="space-y-2">
        <DashPanel title="Pending Orders">
          <div className="grid grid-cols-3">
            <PendingCol title="Imports" total="0" rows={[
              { label: 'Not Arrived', n: '0' },
              { label: 'Non-Traceable', n: '0' },
              { label: 'Arrived', n: '0' },
              { label: 'On Hold', n: '0' },
              { label: 'Available for Delivery', n: '0' },
            ]} />
            <PendingCol title="Exports" total="0" rows={[
              { label: 'On Hold', n: '0' },
              { label: 'Available for Delivery', n: '0' },
              { label: 'Pending Doc Cutoff Date', n: '0' },
              { label: 'Pending Cutoff Date', n: '0' },
              { label: 'Rolling Cutoffs', n: '0' },
            ]} />
            <PendingCol title="Domestic / Repo" total="0" rows={[
              { label: 'Available for Delivery', n: '0' },
              { label: 'Dry Run Pro #s', n: '0', active: true },
            ]} />
          </div>
        </DashPanel>

        <DashPanel title="Tracking">
          <Matrix
            name="Tracking"
            head={['4+', '3', '2', '1', '0', '<0', 'Total']}
            rows={[
              { label: 'ETA', cells: [{ n: '8,204' }, { n: '538' }, { n: '877' }, { n: '1,031', heat: 'cool' }, { n: '849', heat: 'warm' }, { n: '551', heat: 'hot' }, { n: '12,050' }] },
              { label: 'Rail/Port LFD', cells: [{ n: '483' }, { n: '32' }, { n: '149' }, { n: '232', heat: 'cool' }, { n: '79', heat: 'warm' }, { n: '137', heat: 'hot' }, { n: '1,112' }] },
              { label: 'Doc Cutoff', cells: [{ n: '2,801' }, { n: '6' }, { n: '0' }, { n: '61', heat: 'cool' }, { n: '7', heat: 'warm' }, { n: '47', heat: 'hot' }, { n: '2,922' }] },
              { label: 'Cutoff', cells: [{ n: '4,410' }, { n: '0' }, { n: '2' }, { n: '76', heat: 'cool' }, { n: '12', heat: 'warm' }, { n: '7', heat: 'hot' }, { n: '4,507' }] },
              { label: 'Per Diem', cells: [{ n: '2,750' }, { n: '63' }, { n: '34' }, { n: '318', heat: 'cool' }, { n: '190', heat: 'warm' }, { n: '1,118', heat: 'hot' }, { n: '4,473' }] },
              { label: 'Whse LFD', cells: [{ n: '583' }, { n: '2' }, { n: '10' }, { n: '19', heat: 'cool' }, { n: '10', heat: 'warm' }, { n: '207', heat: 'hot' }, { n: '831' }] },
            ]}
          />
        </DashPanel>

        <DashPanel title="Delivery Schedule">
          <Matrix
            name="Delivery Schedule"
            head={['M', 'Tu', 'W', 'Th', 'F', 'S', 'Su', 'Total']}
            rows={[
              { label: '08/24 - 08/30', group: true, cells: [{ n: '1,516' }, { n: '1,539' }, { n: '1,557' }, { n: '1,646' }, { n: '1,371' }, { n: '192' }, { n: '1' }, { n: '7,146' }] },
              { label: 'Not Dispatched', cells: [{ n: '0' }, { n: '0' }, { n: '1', heat: 'hot' }, { n: '44', heat: 'warm' }, { n: '542' }, { n: '181' }, { n: '0' }, { n: '755' }] },
              { label: 'No Outgate', cells: [{ n: '0' }, { n: '0' }, { n: '1', heat: 'hot' }, { n: '34', heat: 'warm' }, { n: '265' }, { n: '8' }, { n: '0' }, { n: '300' }] },
              { label: '08/31 - 09/06', group: true, cells: [{ n: '756' }, { n: '451' }, { n: '319' }, { n: '256' }, { n: '137' }, { n: '3' }, { n: '1' }, { n: '1,779' }] },
              { label: 'Not Dispatched ', cells: [{ n: '680' }, { n: '407' }, { n: '287' }, { n: '241' }, { n: '132' }, { n: '3' }, { n: '1' }, { n: '1,622' }] },
            ]}
          />
        </DashPanel>

        <DashPanel title="Problem Resolution - Warehousing">
          <DashRows cols={2} cells={[
            { label: 'Missing MR/BL Line Items', n: '772', heat: 'hot' }, { label: 'Per Diem LFD ≤ Today, Not Co…', n: '5', heat: 'hot' },
            { label: 'Non-GWI MR Line Items', n: '3', heat: 'hot' }, { label: 'Cutoff ≤ Today, Not Complete', n: '3', heat: 'hot' },
            { label: 'Not Arrived', n: '19', heat: 'hot' }, { label: 'Loaded/Unloaded and Not S…', n: '14', heat: 'hot' },
            { label: 'Checked In and Not Complete', n: '65', heat: 'hot' }, { label: 'Completed and Not Released', n: '0' },
            { label: 'Delivery < Today, Not Compl…', n: '70', heat: 'hot' }, { label: 'Whse Transfer Missing MR', n: '0' },
            { label: 'Load Date < Today, Not Com…', n: '19', heat: 'hot' }, { label: 'Whse Transfer Not Arrived', n: '0' },
          ]} />
        </DashPanel>
      </div>
    </div>
  </div>
);

const modalAndDrawer = () => (
  <div data-theme-part="Page" className="relative bg-app p-4">
    <div className="pointer-events-none select-none opacity-60">
      <Grid
        title="Templates"
        columns={['Template', 'Owner', 'Status']}
        rows={[
          ['Standard Import', 'j.duplechain', <Pill tone="ok">Active</Pill>],
          ['Empty Return', 'l.tran', <Pill tone="ok">Active</Pill>],
        ]}
      />
    </div>

    {/* The scrim is a token too, and is the thing people forget to check. */}
    <div data-theme-part="Modal scrim" className="absolute inset-0 bg-scrim" />

    <div className="relative mx-auto w-full max-w-md overflow-hidden rounded border border-rule bg-cmp-modal shadow-lg">
      <div
        data-theme-part="Modal header"
        className="flex items-center justify-between border-b border-cmp-modal-header-edge bg-cmp-modal-header px-3 py-2"
      >
        <span className="text-sm font-semibold text-cmp-modal-header-fg">Confirm dispatch</span>
        <span className="text-cmp-modal-header-muted">✕</span>
      </div>
      <div className="space-y-2 p-3">
        <p className="text-xs text-body">
          Dispatch <strong className="text-heading">H0412887</strong> to R. Alvarez?
        </p>
        <div className="rounded bg-warn px-2 py-1.5 text-xs text-warn-fg">
          The appointment is within 2 hours.
        </div>
        <Field label="Notes" value="" />
      </div>
      <div className="flex justify-end gap-2 border-t border-hairline bg-sunken px-3 py-2">
        <GhostButton>Cancel</GhostButton>
        <PrimaryButton>Dispatch</PrimaryButton>
      </div>
    </div>

    <div className="relative mt-4 overflow-hidden rounded border border-rule bg-cmp-drawer">
      <div
        data-theme-part="Drawer header"
        className="flex items-center justify-between bg-cmp-drawer-header px-3 py-2"
      >
        <span className="text-sm font-semibold text-cmp-drawer-header-fg">Driver · R. Alvarez</span>
        <span className="text-cmp-drawer-header-fg opacity-75">✕</span>
      </div>
      <div className="space-y-1.5 p-3">
        <Field label="Unit" value="4471" readOnly />
        <Field label="Terminal" value="Houston" />
        <div className="pt-1"><Pill tone="ok">Available</Pill></div>
      </div>
    </div>
  </div>
);

/**
 * The shell is the one scene that cannot be written in Tailwind classes: the
 * real sidebar and top bar read their colours from custom properties set in
 * CollapsibleDashboard's CSS module, so there is no `bg-…` class to copy. The
 * inline vars here are the same properties the real chrome uses, and the
 * data-theme-token attributes are what let the inspector still name them.
 */
const shell = () => (
  <div data-theme-part="Page" className="flex min-h-[22rem] bg-app">
    <div
      data-theme-part="Sidebar"
      data-theme-token="Background:cmp-sidebar-bg, Border:cmp-sidebar-border"
      className="w-44 shrink-0 border-r py-2"
      style={{
        background: 'rgb(var(--cmp-sidebar-bg))',
        borderColor: 'rgb(var(--cmp-sidebar-border))',
      }}
    >
      <div
        data-theme-token="Text:cmp-sidebar-muted"
        className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide"
        style={{ color: 'rgb(var(--cmp-sidebar-muted))' }}
      >
        Dispatching
      </div>
      {['Dispatch Board', 'At Risk Monitor', 'Planner', 'Templates'].map((item, i) => (
        <div
          key={item}
          data-theme-part={i === 1 ? 'Sidebar item (active)' : 'Sidebar item'}
          data-theme-token={i === 1 ? 'Text:cmp-sidebar-active-fg' : 'Text:cmp-sidebar-fg'}
          className={`px-3 py-1.5 text-xs ${i === 1 ? 'font-semibold' : ''}`}
          style={{ color: `rgb(var(--cmp-sidebar-${i === 1 ? 'active-fg' : 'fg'}))` }}
        >
          {item}
        </div>
      ))}
    </div>
    <div className="flex-1">
      <div
        data-theme-part="Top bar"
        data-theme-token="Background:cmp-topbar-bg, Text:cmp-topbar-fg, Border:cmp-topbar-border"
        className="flex items-center justify-between border-b px-3 py-2"
        style={{
          background: 'rgb(var(--cmp-topbar-bg))',
          color: 'rgb(var(--cmp-topbar-fg))',
          borderColor: 'rgb(var(--cmp-topbar-border))',
        }}
      >
        <span className="text-sm font-semibold">FastCMS</span>
        <span className="text-xs opacity-75">j.duplechain</span>
      </div>
      <div className="p-4">
        <Card title="Panel on the page background">
          <p className="text-xs text-body">
            The surfaces stack: page background, then panel, then the header strip inside it.
          </p>
          <p className="mt-1 text-xs text-muted">Muted text sits on the panel.</p>
        </Card>
      </div>
    </div>
  </div>
);

export const SCENES: Scene[] = [
  { id: 'at-risk', label: 'At Risk Monitor', hint: 'Summary tiles, wide grid, risk stripes', render: atRiskMonitor },
  { id: 'pro', label: 'Pro Detail', hint: 'Dense form, legs grid, tabs, cards', render: proDetail },
  { id: 'dashboard', label: 'LC Dashboards', hint: 'Count panels, heat badges, matrices', render: dashboard },
  { id: 'modal', label: 'Modal & Drawer', hint: 'Overlays, scrim, footers', render: modalAndDrawer },
  { id: 'shell', label: 'Nav & Top Bar', hint: 'App chrome, surface stack', render: shell },
];

export default SCENES;
